import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import ModalBase from 'LuxKit/Modal/ModalBase'
import { StyledModalBody } from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Separator from 'LuxKit/Separator'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import AspectRatio from 'components/utils/AspectRatio'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useOfferComparison from 'hooks/Offers/useOfferComparison'
import { rem } from 'polished'
import React, { useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import CellContent from 'components/OfferComparison/cells/CellContent'
import OfferComparisonModalFooter from 'components/OfferComparison/OfferComparisonModalFooter'
import ViewOfferButton from 'components/OfferComparison/ViewOfferButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import TextInput from 'components/Common/Form/Input/TextInput'
import OfferComparisonModalBody from 'components/OfferComparison/OfferComparisonModalBodyYScroller'
import { useElementHeight } from 'hooks/useElementSize'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'
import useHeldValue from 'hooks/useHeldValue'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import { LABEL_HEIGHT } from 'LuxKit/Label/constants'
import { forceElementToFirstPosition } from 'lib/array/arrayUtils'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'

const imageParams: ImageParams = {
  quality: 'best',
  fit: 'center',
  mobileWidth: '578px',
  tabletWidth: '670px',
  desktopWidth: '410px',
  dpr: 2,
}

// In case we can't get the footer height
const FOOTER_HEIGHT_FALLBACK = 124

const ModalBodyXScroller = styled(StyledModalBody)`
  overflow-y: unset;
  overflow-x: auto;
`

const Row = styled(Group)`
  align-items: stretch;

  & + & {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }

  @supports selector(:has(${CellContent})) {
    &:not(:has(${CellContent}:not(:empty))) {
      display: none;
    }
  }
`

const Cell = styled.div`
  flex: 1;
  min-width: ${rem(240)};
  padding-block: ${rem(12)};

  ${Row}:first-child & {
    padding-top: 0;
  }

  ${Row}:last-child & {
    padding-bottom: 0;
  }
`

const XScrollerContent = styled.div`
  position: relative;
  width: fit-content;
  min-width: 100%;
  height: 100%;
`

const EmptyCellIndicator = styled.div`
  height: 100%;
  display: none;
  align-items: center;
  color: ${props => props.theme.palette.neutral.default.five};


  &:only-child, ${CellContent}:empty + & {
    display: flex;
  }
`

const LabelContainer = styled.div`
  min-height: ${LABEL_HEIGHT};
`

function reorderImagesWithLeadFirst(images: Array<App.Image>, imageId: string): Array<App.Image> {
  const leadImage = images.find(image => image.id === imageId)
  return forceElementToFirstPosition(images, leadImage)
}

interface Props {
  mode: 'user-cart' | 'spoofed-user-cart'
}

function MultiCartOfferComparisonModal(props: Props) {
  const intl = useIntl()
  const { mode } = props

  const multiCartActorRef = MultiCartContext.useActorRef()
  const { activeCartId, cartActiveComparisons, cartSpoofedSelections, isCreating } = useSelector(multiCartActorRef, (snapshot) => ({
    activeCartId: snapshot.context.activeCartId,
    cartActiveComparisons: snapshot.context.cartActiveComparisons.status === 'loaded' && snapshot.context.activeCartId
      ? snapshot.context.cartActiveComparisons.data[snapshot.context.activeCartId] || []
      : [],
    cartSpoofedSelections: snapshot.context.cartSpoofedSelections.status === 'loaded' && snapshot.context.activeCartId
      ? snapshot.context.cartSpoofedSelections.data[snapshot.context.activeCartId] || []
      : [],
    isCreating: snapshot.matches('creatingCartOfferComparison'),
  }))

  const selections = mode === 'user-cart' ? cartActiveComparisons : cartSpoofedSelections

  const hasSentToCustomer = cartActiveComparisons &&
    cartActiveComparisons.length > 0 &&
    cartActiveComparisons.length === cartSpoofedSelections.length &&
    cartActiveComparisons.every((item, index) => item.selectionId === cartSpoofedSelections[index].selectionId)

  const modalElement = useModalElementContext()

  const close = () => { modalElement.resolve() }

  const _comparisonResult = useOfferComparison(selections)

  // If we've gone from having offers selected to not having them, the user has just deselected the last one and the modal will close.
  // In that case, keep the previous result displaying while the modal transitions out.
  const comparisonResult = useHeldValue(
    _comparisonResult,
    (current, held) => (
      held.status === 'success' && held.comparison.options.length > 0 &&
      current.status === 'success' && current.comparison.options.length === 0
    ),
  )

  const hasCustomCSAgentLabel = selections.some(selection => selection.label)

  const footerRef = useRef<HTMLDivElement>(null)
  const footerHeight = useElementHeight(footerRef) ?? FOOTER_HEIGHT_FALLBACK
  const spacerHeight = comparisonResult.status === 'success' ? footerHeight : 0

  const offerCount = comparisonResult.status === 'success' ? comparisonResult.comparison.options.length : selections.length

  let modalSize: React.ComponentProps<typeof ModalBase>['size'] = 'L'
  if (offerCount === 1) {
    modalSize = 'S'
  } else if (comparisonResult.status === 'success' && offerCount > 2) {
    modalSize = 'XL'
  }

  const updateOfferComparisonSelection = (selectionId: string | undefined, updates: Partial<App.OfferComparisonSelection>) => {
    const comparisonItem = cartSpoofedSelections.find(s => s.selectionId === selectionId)

    if (!comparisonItem || !activeCartId) return

    const newComparisonItem: App.OfferComparisonSelection = { ...comparisonItem, ...updates }
    multiCartActorRef.send({
      type: 'replace cart offer comparison selection',
      cartId: activeCartId,
      selection: newComparisonItem,
    })
  }

  const onChangeCustomLabel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectionId = e.currentTarget.dataset.selectionid
    const label = e.currentTarget.value

    return updateOfferComparisonSelection(selectionId, { label })
  }

  const onChangeImage = (_: number, image: App.Image | undefined, selectionId: string) => {
    if (!image?.id) return

    return updateOfferComparisonSelection(selectionId, { leadOfferImageId: image.id })
  }

  const onCarouselImageChange = (selectionId: string) => (index: number, image?: App.Image) => {
    onChangeImage(index, image, selectionId)
  }

  const onSendToCustomer = () => {
    if (!activeCartId) return
    multiCartActorRef.send({ type: 'create cart offer comparison', cartId: activeCartId })
  }

  return (
    <ModalBase size={modalSize}>
      <ModalHeader title={intl.formatMessage({ id: 'k6JEoMGwbMGPCQ4cwYwA', defaultMessage: 'Comparison', description: 'Modal header title for offer comparison on the offer listing or detail page' })} onCloseButtonClick={close} />
      <ModalBodyXScroller>
        <XScrollerContent>
          <OfferComparisonModalBody>
            <ModalContent>
              {comparisonResult.status === 'loading' && <LoadingIndicator inline />}

              {comparisonResult.status === 'error' && <MessageBanner kind="critical">{comparisonResult.message}</MessageBanner>}

              {comparisonResult.status === 'success' && <>
                <Row direction="horizontal" gap={16}>
                  {comparisonResult.comparison.options.map(option => (
                    <Cell key={option.id}>
                      <CellContent>
                        <VerticalSpacer gap={12}>
                          {mode === 'user-cart' && hasCustomCSAgentLabel && (
                            <LabelContainer>
                              {option.selection.label && (
                                <PromotionLabel variant="default">{option.selection.label}</PromotionLabel>
                              )}
                            </LabelContainer>
                          )}
                          {mode === 'spoofed-user-cart' && (
                            <TextInput
                              label={intl.formatMessage({ id: 'Bqxqef-D2yltFsLOGAgM', defaultMessage: 'Label', description: 'Form field label for custom comparison label input' })}
                              name="label"
                              maxLength={25}
                              placeholder={intl.formatMessage({ id: 'L5TRC8RDB9XQhJkPXvga', defaultMessage: 'e.g. Best value', description: 'Placeholder for custom comparison label input on the form' })}
                              data-selectionid={option.selection.selectionId}
                              onChange={onChangeCustomLabel}
                              disabled={hasSentToCustomer || isCreating}
                            />
                          )}
                          <AspectRatio ratio="3:2">
                            <ImageCarousel
                              images={mode === 'user-cart' && option.selection.leadOfferImageId ? reorderImagesWithLeadFirst(option.images, option.selection.leadOfferImageId) : option.images}
                              arrowBehaviour="interaction-visible"
                              imageParams={imageParams}
                              topLeft={<Group direction="vertical" gap={4}>
                                {mode === 'spoofed-user-cart' && <StatusLabel variant="added">{hasSentToCustomer ? intl.formatMessage({ id: '-qYmdxTWZwJ3uYAg1PLc', defaultMessage: 'Lead photo', description: 'Status label when photo has been sent as lead in the shopping cart' }) : intl.formatMessage({ id: 'IK9GE3VC-t2PGnW3APtG', defaultMessage: 'Lead with this photo', description: 'Status label prompting to set photo as lead in the gallery view' })}</StatusLabel>}
                                <ProductTypeLabel productType={option.offer.productType} />
                              </Group>}
                              topRight={<TripGuard>
                                <BookmarkButton offer={option.offer} withLabel="never" />
                              </TripGuard>}
                              onImageChange={!hasSentToCustomer && !isCreating && mode === 'spoofed-user-cart' ? onCarouselImageChange(option.selection.selectionId) : undefined}
                            />
                          </AspectRatio>
                          <VerticalSpacer gap={4}>
                            <Caption variant="medium">{option.location}</Caption>
                            <BodyText variant="medium" weight="bold">{option.name}</BodyText>
                            {option.packageName && <BodyText variant="medium">{option.packageName}</BodyText>}
                          </VerticalSpacer>
                        </VerticalSpacer>
                      </CellContent>
                    </Cell>
                  ))}
                </Row>

                {comparisonResult.comparison.rows.map(row => (
                  <Row key={row} direction="horizontal" gap={16}>
                    {comparisonResult.comparison.options.map(offer => (
                      <Cell key={offer.id}>
                        {offer.rows[row]?.element}
                        <EmptyCellIndicator aria-hidden><Separator variant="dash" /></EmptyCellIndicator>
                      </Cell>
                    ))}
                  </Row>
                ))}
              </>}
            </ModalContent>
            <div style={{ height: spacerHeight }}></div>
          </OfferComparisonModalBody>

          {mode !== 'spoofed-user-cart' && (
            <OfferComparisonModalFooter ref={footerRef}>
              {comparisonResult.status === 'success' && <Row direction="horizontal" gap={16}>
                {comparisonResult.comparison.options.map(option => (
                  <Cell key={option.id}>
                    <CellContent>
                      <VerticalSpacer gap={4}>
                        <ViewOfferButton option={option} />
                      </VerticalSpacer>
                    </CellContent>
                  </Cell>
                ))}
              </Row>}
            </OfferComparisonModalFooter>
          )}
        </XScrollerContent>
      </ModalBodyXScroller>
      {mode === 'spoofed-user-cart' && <ModalFooter
        primaryActionProps={{
          children: hasSentToCustomer ? intl.formatMessage({ id: 'aZzuLcB4whBlHdwgrGrB', defaultMessage: 'Sent', description: 'Button label after comparison has been sent to customer' }) : intl.formatMessage({ id: 'TU82p_a6rpDMHuaTQUlz', defaultMessage: 'Send to customer', description: 'Action button label to send offer comparison to customer in the shopping cart' }),
          onClick: onSendToCustomer,
          disabled: hasSentToCustomer || isCreating,
        }}
      />}
    </ModalBase>
  )
}

export default MultiCartOfferComparisonModal
